import { Injectable, signal } from '@angular/core';
import { createClient, SupabaseClient } from '@supabase/supabase-js';
import { environment } from '../../environments/environment';

const TABLE_MAP: Record<string, string> = {
  '/employees': 'employers',
  '/vendors': 'vendors',
  '/team': 'team_members',
  '/passengers': 'users',
  '/drivers': 'drivers',
  '/drivers/pending': 'drivers_pending',
  '/trips': 'trips',
  '/trips/airport': 'trips',
  '/payments': 'payments',
  '/payments/driver': 'driver_payments',
  '/bookings': 'bookings',
  '/service-requests': 'service_requests',
  '/ratings': 'ratings',
  '/reviews': 'reviews',
  '/documents': 'documents',
  '/categories': 'categories',
  '/locations': 'locations',
  '/geo/countries': 'geo_countries',
  '/geo/states': 'geo_states',
  '/geo/cities': 'geo_cities',
  '/promos': 'promos',
  '/requests': 'requests',
  '/activity/steps': 'activity_steps',
  '/notifications/history': 'notification_history',
  '/tiers': 'tiers',
  '/fee-history': 'fee_history',
  '/payment-methods': 'payment_methods',
  '/stats/revenue': 'stats_revenue',
  '/leave-requests': 'leave_requests',
  '/payslips': 'payslips',
  '/employee-taxes': 'employee_taxes',
  '/employee-deductions': 'employee_deductions',
  '/employer-benefits': 'employer_benefits',
  '/absence-plans': 'absence_plans',
};

const SUB_RESOURCE_MAP: Record<string, { table: string; fkField: string }> = {
  trips: { table: 'trips', fkField: '' },
  payments: { table: 'payments', fkField: '' },
  ratings: { table: 'ratings', fkField: '' },
  reviews: { table: 'reviews', fkField: '' },
  documents: { table: 'documents', fkField: 'driver_id' },
  'fee-history': { table: '', fkField: '' },
};

const ADDRESS_FIELDS = ['address_street', 'address_country', 'address_state', 'address_city', 'address_zip'];
const TABLES_WITH_ADDRESS = ['users', 'drivers'];

const REGION_TO_COUNTRIES: Record<string, string[]> = {
  NA: ['US', 'CA', 'MX'],
  EU: ['GB', 'DE', 'FR', 'IT', 'ES', 'TR'],
  ME: ['AE', 'SA'],
  AF: ['NG', 'ZA', 'EG', 'KE'],
  OC: ['AU'],
  LA: ['BR', 'AR', 'CO', 'CL'],
};

interface GeoColDef {
  region?: string;
  country: string[];
  state: string[];
  city: string[];
}

const DEFAULT_GEO_COLS: GeoColDef = { country: ['country'], state: [], city: [] };

const GEO_COLUMNS: Record<string, GeoColDef> = {
  users:           { country: ['country_code', 'address_country'], state: ['address_state'], city: ['address_city'] },
  drivers:         { country: ['country_code', 'address_country'], state: ['address_state'], city: ['address_city'] },
  drivers_pending: { country: [], state: [], city: [] },
  trips:           { country: ['country'], state: [], city: ['city'] },
  promos:          { country: ['country'], state: ['state'], city: ['city'] },
  employers:       { region: 'region', country: ['country'], state: ['state'], city: ['city'] },
};

function toSnakeCase(str: string): string {
  return str.replace(/[A-Z]/g, c => '_' + c.toLowerCase());
}

function toCamelCase(str: string): string {
  return str.replace(/_([a-z])/g, (_, c) => c.toUpperCase());
}

function rowToFrontend(row: Record<string, any>, table: string): Record<string, any> {
  const result: Record<string, any> = {};
  const addressObj: Record<string, any> = {};
  let hasAddress = false;

  for (const [key, value] of Object.entries(row)) {
    if (TABLES_WITH_ADDRESS.includes(table) && key.startsWith('address_')) {
      const subKey = key.replace('address_', '');
      addressObj[subKey] = value;
      hasAddress = true;
      continue;
    }
    result[toCamelCase(key)] = value;
  }

  if (hasAddress) {
    result['address'] = addressObj;
  }

  return result;
}

const SKIP_FIELDS = new Set([
  'profilePhoto', 'carPhoto', 'driversLicense', 'tlcLicense',
  'insurance', 'vehicleRegistration', 'document', 'attachment',
]);

function bodyToDb(body: Record<string, any>, table: string): Record<string, any> {
  const result: Record<string, any> = {};
  for (const [key, value] of Object.entries(body)) {
    if (key === 'id') continue;
    if (SKIP_FIELDS.has(key)) continue;
    if (value instanceof File || value instanceof Blob) continue;

    // Dot-notation keys from CrudForm: "address.city" → "address_city"
    if (key.includes('.')) {
      const dbCol = key.replace(/\./g, '_');
      result[toSnakeCase(dbCol)] = value;
      continue;
    }

    // Nested address object from detail pages
    if (key === 'address' && typeof value === 'object' && value !== null) {
      if (value.street) result['address_street'] = value.street;
      if (value.country) result['address_country'] = value.country;
      if (value.state) result['address_state'] = value.state;
      if (value.city) result['address_city'] = value.city;
      if (value.zip) result['address_zip'] = value.zip;
      continue;
    }

    result[toSnakeCase(key)] = value;
  }
  return result;
}

/**
 * Resolves an API path to { basePath, table, id? }.
 * Tries longest matching prefix first.
 */
function resolveApiPath(apiPath: string): { basePath: string; table: string; id?: string; isAirport?: boolean } | null {
  const clean = apiPath.startsWith('/') ? apiPath : '/' + apiPath;

  // Try matching with trailing ID: /geo/countries/5 → /geo/countries + 5
  const idMatch = clean.match(/^(.+?)\/(\d+)$/);
  if (idMatch) {
    const [, prefix, id] = idMatch;
    const table = TABLE_MAP[prefix];
    if (table) return { basePath: prefix, table, id };
  }

  // Direct table match (no ID)
  const table = TABLE_MAP[clean];
  if (table) {
    const isAirport = clean === '/trips/airport';
    return { basePath: clean, table, isAirport };
  }

  return null;
}

const SEARCHABLE_COLUMNS: Record<string, string[]> = {
  users: ['first_name', 'last_name', 'email', 'phone'],
  drivers: ['name', 'first_name', 'last_name', 'email', 'phone'],
  drivers_pending: ['name', 'first_name', 'last_name', 'email'],
  trips: ['passenger_name', 'driver_name', 'transaction_id', 'pickup', 'dropoff'],
  payments: ['passenger_name', 'driver_name', 'transaction_id'],
  driver_payments: ['driver_name', 'driver_email', 'transaction_id'],
  bookings: ['passenger_name', 'driver_name', 'transaction_id'],
  service_requests: ['name', 'email', 'phone'],
  ratings: ['driver_name', 'passenger_name'],
  reviews: ['driver_name', 'passenger_name', 'review'],
  documents: ['driver_name', 'file_name'],
  categories: ['name', 'service_code'],
  locations: ['name', 'city_name'],
  geo_countries: ['name', 'sort_name'],
  geo_states: ['name', 'country_name'],
  geo_cities: ['name', 'state_name'],
  promos: ['code', 'city'],
  employers: ['name', 'email'],
  vendors: ['name', 'email', 'contact_name'],
  team_members: ['first_name', 'last_name', 'email'],
  requests: ['requester_name', 'description'],
  activity_steps: ['performed_by', 'description'],
  notification_history: ['title', 'recipient_name'],
  tiers: ['name'],
  leave_requests: ['employee_name', 'reason', 'leave_type'],
  payslips: ['reference_number'],
  employee_taxes: ['description', 'tax_type'],
  employee_deductions: ['description', 'deduction_type'],
  employer_benefits: ['description'],
  absence_plans: ['plan_name', 'plan_type'],
};

@Injectable({ providedIn: 'root' })
export class SupabaseService {
  private client: SupabaseClient | null = null;
  readonly enabled = signal(false);

  constructor() {
    const env = environment as any;
    if (env.supabaseUrl && env.supabaseKey) {
      this.client = createClient(env.supabaseUrl, env.supabaseKey);
      this.enabled.set(true);
    }
  }

  getClient(): SupabaseClient | null {
    return this.client;
  }

  isSupabaseTable(apiPath: string): boolean {
    if (!this.enabled()) return false;
    return resolveApiPath(apiPath) !== null;
  }

  resolveTable(apiPath: string) {
    return resolveApiPath(apiPath);
  }

  async getPage(table: string, params: Record<string, string>, extraFilters?: Record<string, any>): Promise<any> {
    const page = parseInt(params['page'] ?? '0', 10);
    const size = parseInt(params['size'] ?? '20', 10);
    const sort = params['sort'];
    const direction = params['direction'] ?? 'asc';
    const search = params['search'] ?? '';

    let query = this.client!.from(table).select('*', { count: 'exact' });

    if (extraFilters) {
      for (const [col, val] of Object.entries(extraFilters)) {
        if (val === 'NOT_NULL') {
          query = query.not(col, 'is', null);
        } else {
          query = query.eq(col, val);
        }
      }
    }

    if (search) {
      const cols = SEARCHABLE_COLUMNS[table] || ['name', 'email'];
      const orClause = cols.map(c => `${c}.ilike.%${search}%`).join(',');
      query = query.or(orClause);
    }

    const geoKeys = new Set(['region', 'country', 'state', 'city']);
    const skipKeys = new Set(['page', 'size', 'sort', 'direction', 'search', ...geoKeys]);
    for (const [key, value] of Object.entries(params)) {
      if (skipKeys.has(key) || !value) continue;
      const dbCol = toSnakeCase(key);
      query = query.eq(dbCol, value);
    }

    // Geo filters — use per-table column mapping to avoid referencing non-existent columns
    const geo = GEO_COLUMNS[table] || DEFAULT_GEO_COLS;
    const regionCode = params['region'];
    const countryCode = params['country'];
    const stateCode = params['state'];
    const cityName = params['city'];

    if (regionCode && (geo.region || geo.country.length)) {
      const countryCodes = REGION_TO_COUNTRIES[regionCode];
      if (geo.region && countryCodes) {
        const parts = [`${geo.region}.eq.${regionCode}`];
        for (const col of geo.country) parts.push(`${col}.in.(${countryCodes.join(',')})`);
        query = query.or(parts.join(','));
      } else if (geo.region) {
        query = query.eq(geo.region, regionCode);
      } else if (countryCodes && geo.country.length) {
        const parts = geo.country.map(col => `${col}.in.(${countryCodes.join(',')})`);
        query = query.or(parts.join(','));
      }
    }
    if (countryCode && geo.country.length) {
      if (geo.country.length === 1) {
        query = query.eq(geo.country[0], countryCode);
      } else {
        query = query.or(geo.country.map(col => `${col}.eq.${countryCode}`).join(','));
      }
    }
    if (stateCode && geo.state.length) {
      if (geo.state.length === 1) {
        query = query.eq(geo.state[0], stateCode);
      } else {
        query = query.or(geo.state.map(col => `${col}.eq.${stateCode}`).join(','));
      }
    }
    if (cityName && geo.city.length) {
      if (geo.city.length === 1) {
        query = query.eq(geo.city[0], cityName);
      } else {
        query = query.or(geo.city.map(col => `${col}.eq.${cityName}`).join(','));
      }
    }

    if (sort) {
      query = query.order(toSnakeCase(sort), { ascending: direction === 'asc' });
    } else {
      query = query.order('id', { ascending: true });
    }

    query = query.range(page * size, (page + 1) * size - 1);

    const { data, count, error } = await query;
    if (error) throw error;

    const content = (data ?? []).map(r => rowToFrontend(r, table));
    const totalElements = count ?? 0;

    return {
      content,
      totalElements,
      totalPages: Math.ceil(totalElements / size),
      size,
      number: page,
    };
  }

  async getById(table: string, id: string | number): Promise<any> {
    const { data, error } = await this.client!
      .from(table)
      .select('*')
      .eq('id', id)
      .single();
    if (error) throw error;
    return rowToFrontend(data, table);
  }

  async create(table: string, body: Record<string, any>): Promise<any> {
    const dbBody = bodyToDb(body, table);
    const { data, error } = await this.client!
      .from(table)
      .insert(dbBody)
      .select()
      .single();
    if (error) throw error;
    return rowToFrontend(data, table);
  }

  async update(table: string, id: string | number, body: Record<string, any>): Promise<any> {
    const dbBody = bodyToDb(body, table);
    const { data, error } = await this.client!
      .from(table)
      .update(dbBody)
      .eq('id', id)
      .select()
      .single();
    if (error) throw error;
    return rowToFrontend(data, table);
  }

  async remove(table: string, id: string | number): Promise<any> {
    const { error } = await this.client!
      .from(table)
      .delete()
      .eq('id', id);
    if (error) throw error;
    return { success: true };
  }

  async getSubResource(
    parentTable: string,
    parentId: string | number,
    childTable: string,
    fkColumn: string,
  ): Promise<any> {
    const { data, error } = await this.client!
      .from(childTable)
      .select('*')
      .eq(fkColumn, parentId);
    if (error) throw error;
    const content = (data ?? []).map(r => rowToFrontend(r, childTable));
    return { content, totalElements: content.length };
  }

  async getAll(table: string): Promise<any[]> {
    const { data, error } = await this.client!.from(table).select('*').order('id');
    if (error) throw error;
    return (data ?? []).map(r => rowToFrontend(r, table));
  }

  async getPaymentMethods(countryCode: string): Promise<any> {
    const { data, error } = await this.client!
      .from('payment_methods')
      .select('*')
      .eq('country_code', countryCode);
    if (error) throw error;
    const methods = (data ?? []).map(r => rowToFrontend(r, 'payment_methods'));
    return {
      countryCode,
      currency: methods[0]?.['currency'] || 'USD',
      methods,
    };
  }

  async getDashboardStats(
    geo: { region?: string; country?: string; state?: string; city?: string },
    range?: { from?: string; to?: string },
  ): Promise<any> {
    let query = this.client!.from('dashboard_city_stats').select('*');
    if (geo.region) query = query.eq('region_code', geo.region);
    if (geo.country) query = query.eq('country_code', geo.country);
    if (geo.state) query = query.eq('state_code', geo.state);
    if (geo.city) query = query.eq('city_name', geo.city);

    const { data, error } = await query;
    if (error) throw error;
    const entries = data ?? [];

    let scale = 1;
    if (range?.from && range?.to) {
      const d1 = new Date(range.from).getTime();
      const d2 = new Date(range.to).getTime();
      if (d2 > d1) {
        const days = Math.ceil((d2 - d1) / 86_400_000) + 1;
        scale = Math.min(1, days / 365);
      }
    }

    const sc = (v: number) => Math.round(v * scale);
    const scf = (v: number) => +(v * scale).toFixed(2);

    const totalRevenue = scf(entries.reduce((s, e) => s + (e.total_revenue || 0), 0));
    const totalAirportFees = scf(entries.reduce((s, e) => s + (e.total_airport_fees || 0), 0));
    const totalMiscFees = scf(entries.reduce((s, e) => s + (e.total_misc_fees || 0), 0));
    const totalCityFees = scf(entries.reduce((s, e) => s + (e.total_city_fees || 0), 0));
    const commissionRevenue = scf(entries.reduce((s, e) => s + (e.commission_revenue || 0), 0));
    const subscriptionRevenue = scf(entries.reduce((s, e) => s + (e.subscription_revenue || 0), 0));
    const totalFees = +(totalAirportFees + totalMiscFees + totalCityFees).toFixed(2);
    const driverPayout = +(totalRevenue - totalFees - commissionRevenue).toFixed(2);

    return {
      totalDrivers: entries.reduce((s, e) => s + (e.total_drivers || 0), 0),
      activeDrivers: entries.reduce((s, e) => s + (e.active_drivers || 0), 0),
      gracePeriod: entries.reduce((s, e) => s + (e.grace_period_drivers || 0), 0),
      totalTrips: sc(entries.reduce((s, e) => s + (e.total_trips || 0), 0)),
      completedTrips: sc(entries.reduce((s, e) => s + (e.completed_trips || 0), 0)),
      totalRevenue,
      totalFees,
      driverPayout,
      commissionRevenue,
      subscriptionRevenue,
      netProfit: +(commissionRevenue + subscriptionRevenue).toFixed(2),
      totalPassengers: entries.reduce((s, e) => s + (e.total_passengers || 0), 0),
      activePassengers: entries.reduce((s, e) => s + (e.active_passengers || 0), 0),
    };
  }
}
